import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell, Check, X, Inbox } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

const KIND_COLOR = {
  need_match:       "#1A8F4A",
  mission_proposed: "#F59E0B",
  booking_confirmed:"#3B82F6",
  booking_cancelled:"#E3655B",
  booking_completed:"#6B4DD9",
  booking_deleted:  "#6B726A",
  default:          "#1A4D2E",
};

export default function NotificationBell({ className = "", testid = "notif-bell" }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState("right");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const fetchNotifs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data?.items || []);
      setUnread(data?.unread || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  // Initial + poll every 60s
  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 60000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`); } catch { /* ignore */ }
  };
  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
  };
  const remove = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch { /* ignore */ }
  };
  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) nav(n.link);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        onClick={() => {
          const r = btnRef.current?.getBoundingClientRect();
          setAlign(r && r.left < 380 ? "left" : "right");
          setOpen((v) => !v);
        }}
        data-testid={testid}
        className="relative w-10 h-10 rounded-full bg-white border border-[#E2E5E0] grid place-items-center text-[#1A4D2E] hover:bg-[#F7F8F6]"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E3655B] text-white text-[10px] font-semibold grid place-items-center" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div ref={popRef} className={`absolute ${align === "left" ? "left-0" : "right-0"} mt-2 w-[360px] max-w-[90vw] bg-white rounded-2xl border border-[#E2E5E0] shadow-xl z-50 overflow-hidden`} data-testid="notif-popover">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E5E0]">
            <div className="text-sm font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Notifications</div>
            <button onClick={markAll} disabled={unread === 0} data-testid="notif-mark-all"
              className="text-xs text-[#1A4D2E] hover:underline disabled:text-[#C5CBC2] inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Tout marquer lu
            </button>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#6B726A]">Chargement…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center" data-testid="notif-empty">
                <Inbox className="w-7 h-7 text-[#C5CBC2] mx-auto mb-2" />
                <div className="text-xs text-[#6B726A]">Aucune notification pour le moment.</div>
              </div>
            ) : items.map((n) => {
              const color = KIND_COLOR[n.kind] || KIND_COLOR.default;
              return (
                <div key={n.id} className={`group flex items-start gap-3 px-4 py-3 border-b last:border-b-0 border-[#F0F2EF] hover:bg-[#F7F8F6] ${n.read ? "" : "bg-[#F4F8F5]"}`} data-testid={`notif-${n.id}`}>
                  <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: n.read ? "#C5CBC2" : color }} />
                  <button onClick={() => handleClick(n)} className="flex-1 min-w-0 text-left">
                    <div className={`text-sm ${n.read ? "text-[#3F4640]" : "font-medium text-[#0F2A1B]"}`}>{n.title}</div>
                    {n.body && <div className="text-xs text-[#6B726A] mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-[#9AA09A] mt-1">{timeAgo(n.created_at)}</div>
                  </button>
                  <button onClick={() => remove(n.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#6B726A] hover:text-[#E3655B] w-5 h-5 grid place-items-center" data-testid={`notif-remove-${n.id}`} aria-label="Supprimer">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
